import { TabContext, TabPanel } from "@mui/lab";
import { Box, Grid, styled, Tab } from "@mui/material";
import { api, types } from "api";
import superhero from "assets/icons/superhero.svg";
import { ColorTabs } from "components/ColorTabs";
import { InfoSnackBar } from "components/InfoSnackBar";
import { Assignment, getStatus } from "helpers/assignment";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { ACTIVITIES_FETCH_ERR } from "messages";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { StudentLeaderboardPageComponent } from "./StudentLeaderboardPageComponent";

const PREFIX = "StudentLeaderBoardPage";

const classes = {
	root: `${PREFIX}-root`,
	tabs: `${PREFIX}-tabs`,
	tabsBar: `${PREFIX}-tabsBar`,
	superheroLogo: `${PREFIX}-superheroLogo`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		padding: 20,
		flexGrow: 1,
	},

	[`& .${classes.tabs}`]: {
		color: "white",
	},

	[`& .${classes.tabsBar}`]: {
		backgroundColor: theme.palette.primary.main,
	},

	[`& .${classes.superheroLogo}`]: {
		width: "100%",
	},
}));

export const StudentLeaderboardPage = (): JSX.Element => {
	const params = useParams<{ groupId: types.UUID }>();
	const { updateLastBreadcrumbs } = useBreadcrumbs();
	const [assignments, setAssignment] = useState<Assignment[]>([]);
	const [err, setErr] = useState(false);
	const componentMounted = useRef(true);
	const [value, setValue] = React.useState<string>("");
	const [myInfo, setMyInfo] = React.useState<types.User>();

	useEffect(() => {
		api
			.getGroup(params.groupId)
			.then((res) => {
				if (!componentMounted.current || res.status !== 200) {
					return;
				}

				updateLastBreadcrumbs([{ name: res.data.name, params }, undefined]);
			})
			.catch(() => {
				setErr(true);
			});

		api
			.getGroupAssignments(params.groupId)
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					const now = new Date();
					const newAssignments = (res.data || []).map((ass) => ({
						...ass,
						status: getStatus(ass, now),
					}));

					setValue(newAssignments[0]?.assignmentId!);
					setAssignment(newAssignments);
				}
			})
			.catch(() => {
				setErr(true);
			});
		api
			.getMyUserData()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				} else {
					setMyInfo(res.data);
				}
			})
			.catch(() => {
				setErr(true);
			});
		return () => {
			componentMounted.current = false;
		};
	}, [params.groupId, assignments, updateLastBreadcrumbs, params]);

	const handleChange = (
		event: React.ChangeEvent<{}>,
		newValue: string,
	): void => {
		setValue(newValue);
	};

	return (
		<Root className={classes.root}>
			<Grid container spacing={2}>
				<Grid item xs={12} lg={9}>
					<Box sx={{ backgroundColor: "white" }}>
						<TabContext value={value}>
							<ColorTabs
								value={value}
								onChange={handleChange}
								variant="scrollable"
								scrollButtons
								className={classes.tabsBar}
							>
								{assignments.map((item: types.Assignment) => (
									<Tab
										key={item.assignmentId}
										id={item.assignmentId}
										label={item.name}
										value={item.assignmentId}
										data-testid="test_tab"
										sx={{ color: "white" }}
										classes={{
											root: classes.tabs,
										}}
									/>
								))}
							</ColorTabs>

							{assignments.map((item) => (
								<TabPanel
									key={item.assignmentId}
									value={item.assignmentId}
									data-testid={item.assignmentId}
								>
									<StudentLeaderboardPageComponent
										assignmentId={value}
										myId={myInfo?.userId}
									/>
								</TabPanel>
							))}
						</TabContext>
					</Box>
				</Grid>
				<Grid item xs={12} lg={3}>
					<img
						data-testid="superhero"
						src={superhero}
						alt="superhero"
						className={classes.superheroLogo}
					/>
				</Grid>
			</Grid>

			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={ACTIVITIES_FETCH_ERR}
				severity="error"
			/>
		</Root>
	);
};
